/* Audit Log tab , read-only table of every admin write captured by the
   backend `log_audit_event()` helper. Newest first. TTL-purged after 90 days. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, RefreshCcw, Ticket, Package, Calendar, KeyRound, Sparkles, Inbox } from "lucide-react";
import { adminApi } from "./adminApi";

// Pretty labels + icon per resource
const RESOURCE_META = {
 ticket_event: { label: "Ticket price", Icon: Ticket, color: "text-amber-300" },
 package: { label: "Package", Icon: Package, color: "text-fuchsia-300" },
 entertainment: { label: "Entertainment", Icon: Sparkles, color: "text-violet-300" },
 blocked_dates: { label: "Availability", Icon: Calendar, color: "text-rose-300" },
 enquiry: { label: "Enquiry", Icon: Inbox, color: "text-cyan-300" },
 admin_password: { label: "Password", Icon: KeyRound, color: "text-emerald-300" },
};

const ACTION_LABEL = {
 update: "Updated",
 create: "Added",
 delete: "Removed",
};

export default function AdminAuditLog({ onUnauthorized, showToast }) {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filter, setFilter] = useState("all");

 const load = useCallback(async () => {
  setLoading(true);
  try {
   const res = await adminApi("/admin/audit-log?limit=300");
   setItems(res.items || []);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load audit log", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast]);

 useEffect(() => { load(); }, [load]);

 const filtered = useMemo(() => {
  if (filter === "all") return items;
  return items.filter((it) => it.resource === filter);
 }, [items, filter]);

 const counts = useMemo(() => {
  const c = { all: items.length };
  for (const it of items) c[it.resource] = (c[it.resource] || 0) + 1;
  return c;
 }, [items]);

 return (
  <div data-testid="admin-tab-audit">
   <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
      <History className="w-3.5 h-3.5" /> Audit Log · {items.length}
     </div>
     <p className="text-white/65 text-sm max-w-2xl">Every change made through the admin dashboard , who, what, when. Entries are kept for 90 days.</p>
    </div>
    <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-audit-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
   </div>

   {/* Filter pills */}
   <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-audit-filters">
    <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors ${filter === "all" ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`} data-testid="admin-audit-filter-all">All ({counts.all || 0})</button>
    {Object.entries(RESOURCE_META).map(([key, meta]) => (
     <button key={key} onClick={() => setFilter(key)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors ${filter === key ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`} data-testid={`admin-audit-filter-${key}`}>
      <meta.Icon className="w-3 h-3" /> {meta.label} ({counts[key] || 0})
     </button>
    ))}
   </div>

   {loading ? (
    <div className="text-white/45 text-sm py-12 text-center">Loading…</div>
   ) : filtered.length === 0 ? (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-16 text-center">
     <div className="text-white/40 text-sm">{items.length === 0 ? "No admin changes recorded yet." : "No entries match this filter."}</div>
    </div>
   ) : (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
     {filtered.map((it, idx) => {
      const meta = RESOURCE_META[it.resource] || { label: it.resource, Icon: History, color: "text-white/70" };
      const Icon = meta.Icon;
      const ts = it.ts ? new Date(it.ts).toLocaleString("en-AU") : "";
      return (
       <div key={it.id || idx} className="px-5 py-4 border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.02]" data-testid={`admin-audit-row-${idx}`}>
        <div className="flex flex-wrap items-start gap-3 justify-between">
         <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`shrink-0 mt-0.5 ${meta.color}`}><Icon className="w-4 h-4" /></div>
          <div className="min-w-0 flex-1">
           <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.22em] font-black ${meta.color}`}>{meta.label}</span>
            <span className="text-white/30 text-[10px]">·</span>
            <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold">{ACTION_LABEL[it.action] || it.action}</span>
            {it.resource_id && (
             <>
              <span className="text-white/30 text-[10px]">·</span>
              <span className="text-white/55 text-[10px] font-mono">{it.resource_id}</span>
             </>
            )}
           </div>
           <div className="text-white text-sm">{it.summary || "(no summary)"}</div>
          </div>
         </div>
         <div className="text-white/40 text-[10px] uppercase tracking-wider whitespace-nowrap">
          {it.actor && <span className="text-white/55 mr-2">{it.actor}</span>}
          {ts}
         </div>
        </div>
       </div>
      );
     })}
    </div>
   )}
  </div>
 );
}
